<script setup>
import { profileHashtags } from '@/lib/profileHashtags';
import { emitter } from "@plugins/mitt";

import { Icon } from "@iconify/vue";

const props = defineProps({
  userInfo: {
    type: Object,
    default: () => ({}),
  },
});

const emits = defineEmits(["onRequestSuccess", "onOpenRoom"]);

const userStore = useUserStore();
const roomStore = useRoomStore();

const tradeSigilsTooltip = `The current cost of 1 Sigil, priced along a bonding curve.`;

const sigilCostTooltip = `The current cost of 1 Sigil, priced along a bonding curve.`;

const sigilStatsTooltip = `Stats on a player's sigils cost, historical price, buy/sells and
              more.`;

const loadingFollow = ref(false);
const buyCost = ref("0.0");
const tradeSigilsDialogRef = ref();
const sigilStatsDialogRef = ref();
const followListDialogRef = ref();
const avatarPreviewDialogRef = ref();

const isMyProfile = computed(() => {
  return userStore.userInfo?.uid === props.userInfo?.uid;
});

const roomUser = computed(() => {
  return roomStore.roomList.find(
    (room) => room.hostIdentity === props.userInfo?.uid
  );
});

const twitterUser = computed(async () => {
  try {
    if (props.userInfo?.twitterId) {
      const { user_profile } = await getUserTwitter(props.userInfo?.twitterId);
      return user_profile;
    }
  } catch (error) {
    console.error("Error fetching Twitter user:", error);
    return {};
  }
});

onMounted(async () => {
  // if (userInfo?.twitterId) {
  //   getUserTwitter(userInfo?.twitterId);
  // }
  await fetchBuyCost();
});

const unrequestFollow = async () => {
  try {
    loadingFollow.value = true;
    await deleteFriendRequest(props.userInfo?.uid);

    emits("onRequestSuccess");
  } catch (error) {
    console.error("Error unrequesting follow:", error);
  } finally {
    loadingFollow.value = false;
  }
};

const unfollow = async () => {
  try {
    loadingFollow.value = true;
    await unfriend(props.userInfo?.uid);

    emits("onRequestSuccess");
  } catch (error) {
    console.error("Error unfollowing:", error);
  } finally {
    loadingFollow.value = false;
  }
};

const acceptFollow = async () => {
  try {
    loadingFollow.value = true;
    await acceptFriendRequest(props.userInfo?.uid);

    emits("onRequestSuccess");
  } catch (error) {
    console.error("Error accepting follow:", error);
  } finally {
    loadingFollow.value = false;
  }
};

const follow = async () => {
  try {
    loadingFollow.value = true;
    await sendFriendRequest(props.userInfo?.uid);

    emits("onRequestSuccess");
  } catch (error) {
    console.error("Error following:", error);
  } finally {
    loadingFollow.value = false;
  }
};

const goToDirectMessage = () => {
  emitter.emit("goToDirectMessage", props.userInfo);
};

const fetchBuyCost = async () => {
  try {
    const response = await getBuyCost(props.userInfo?.uid, "1");

    console.log("fetchBuyCost", response);

    if (response?.cost && response.cost !== "0") {
      buyCost.value = formatEtherString(response.cost);
    } else {
      buyCost.value = "0.0";
    }
  } catch (error) {
    console.error("Error fetching buy cost:", error);

    buyCost.value = "0.0";
  } finally {
  }
};

const buyCostDisplay = computed(() =>
  formatAvaxDisplay(buyCost.value, {
    forceUnit: "AVAX",
    decimalsByUnit: { AVAX: 10 },
  })
);

const openTradeSigilsDialog = () => {
  tradeSigilsDialogRef.value.openDialog();
};

const openSigilStatsDialog = () => {
  sigilStatsDialogRef.value.openDialog();
};

const openUserHubDialog = () => {
  emits("onOpenRoom");
};

const openFollowersDialog = () => {
  if (!props.userInfo?.uid || !followListDialogRef.value) return;

  followListDialogRef.value.openDialog({
    uid: props.userInfo.uid,
    type: "followers",
  });
};

const openFollowingDialog = () => {
  if (!props.userInfo?.uid || !followListDialogRef.value) return;

  followListDialogRef.value.openDialog({
    uid: props.userInfo.uid,
    type: "following",
  });
};

const openAvatarPreview = () => {
  const imageUrl = props.userInfo?.profileImageURL;
  if (!imageUrl || !avatarPreviewDialogRef.value) return;

  avatarPreviewDialogRef.value.openDialog(imageUrl);
};
</script>

<template>
  <v-card class="!tw-rounded-2xl">
    <v-card-text class="tw-text-white tw-flex tw-flex-col tw-gap-3">
      <div class="md:tw-px-6 tw-flex tw-gap-4 !tw-items-stretch">
        <div class="tw-w-[30%] tw-max-w-[180px]">
          <div
            class="tw-w-full tw-rounded-full tw-overflow-hidden tw-cursor-pointer hover:tw-opacity-90 tw-transition"
            @click="openAvatarPreview"
            @keydown.enter.prevent="openAvatarPreview"
            @keydown.space.prevent="openAvatarPreview"
          >
            <user-avatar
              :imageUrl="userInfo?.profileImageURL"
              :enableProfileDialog="false"
              class="!tw-w-[180px] !tw-aspect-square tw-max-w-full !tw-h-auto"
            />
          </div>
        </div>

        <div
          class="tw-flex-auto tw-flex tw-flex-col tw-justify-between !tw-self-stretch"
        >
          <div>
            <div class="tw-text-2xl tw-font-bold">
              {{ userInfo?.firstName }}
            </div>

            <!-- <div class="tw-text-[10px]">
            <span class="tw-font-bold">
              @{{ userStore.particleUserInfo?.name }}</span
            >

            <span v-if="providerPN === 'twitter'">
              on
              <v-icon size="12" class="">garden-twitter-stroke-12</v-icon>
            </span>
          </div> -->
          </div>

          <div>
            <div class="tw-text-lg tw-font-normal">
              {{ userInfo?.oneLiner }}
            </div>

            <div class="tw-text-lg tw-font-normal">
              {{ profileHashtags(userInfo?.hashTagIdentifiers) }}
            </div>
          </div>
        </div>
      </div>

      <div class="md:tw-px-9 tw-grid tw-grid-cols-6 tw-gap-1">
        <div
          class="tw-col-start-2 tw-col-span-2 tw-rounded-3xl tw-uppercase tw-border-[#4D4D4D] tw-border-solid tw-p-4 tw-text-center tw-text-sm tw-cursor-pointer hover:tw-border-white/70 focus:tw-border-white/70 tw-transition"
          @click="openFollowersDialog"
          @keydown.enter.prevent="openFollowersDialog"
          @keydown.space.prevent="openFollowersDialog"
        >
          <div class="tw-font-bold tw-text-[18px]">
            {{ userInfo?.followCount || 0 }}
          </div>
          followers
        </div>

        <div
          class="tw-col-span-2 tw-rounded-3xl tw-uppercase tw-border-[#4D4D4D] tw-border-solid tw-p-4 tw-text-center tw-text-sm tw-cursor-pointer hover:tw-border-white/70 focus:tw-border-white/70 tw-transition"
          @click="openFollowingDialog"
          @keydown.enter.prevent="openFollowingDialog"
          @keydown.space.prevent="openFollowingDialog"
        >
          <div class="tw-font-bold tw-text-[18px]">
            {{ userInfo?.followingCount || 0 }}
          </div>
          following
        </div>

        <!-- <div
          class="tw-rounded-3xl tw-uppercase tw-border-[#4D4D4D] tw-border-solid tw-p-4 tw-text-center tw-text-sm"
          @click="linkXDialogRef.openDialog()"
        >
          Link
          <div>
            Your
            <v-icon size="12" class=""> garden-twitter-stroke-12 </v-icon>
          </div>

          <link-your-x-dialog ref="linkXDialogRef"></link-your-x-dialog>
        </div> -->
      </div>

      <div
        class="md:tw-px-9 tw-grid tw-grid-cols-2 tw-gap-1"
        v-if="!isMyProfile"
      >
        <v-btn
          variant="outlined"
          color="white"
          class="tw-rounded-3xl tw-uppercase tw-border-solid tw-text-center tw-text-sm"
          @click="unrequestFollow"
          v-if="userInfo?.followStatus === 1"
          :disabled="loadingFollow"
          :loading="loadingFollow"
        >
          Following
        </v-btn>
        <v-btn
          variant="outlined"
          color="white"
          class="tw-rounded-3xl tw-uppercase tw-border-solid tw-text-center tw-text-sm"
          @click="unfollow"
          v-else-if="userInfo?.followStatus === 2"
          :disabled="loadingFollow"
          :loading="loadingFollow"
        >
          Unfollow
        </v-btn>
        <v-btn
          variant="outlined"
          color="white"
          class="tw-rounded-3xl tw-uppercase tw-border-solid tw-text-center tw-text-sm"
          @click="acceptFollow"
          v-else-if="userInfo?.followStatus === 3"
          :disabled="loadingFollow"
          :loading="loadingFollow"
        >
          Accept
        </v-btn>
        <v-btn
          variant="outlined"
          color="white"
          class="tw-rounded-3xl tw-uppercase tw-border-solid tw-text-center tw-text-sm"
          @click="follow"
          v-else
        >
          Follow
        </v-btn>

        <v-btn
          variant="outlined"
          color="white"
          class="tw-rounded-3xl tw-uppercase tw-border-solid tw-text-center tw-text-sm"
          @click="goToDirectMessage"
        >
          Message
        </v-btn>
      </div>

      <div class="tw-flex tw-justify-center tw-py-5">
        <room
          :room="roomUser"
          v-if="roomUser"
          @onRoomClicked="openUserHubDialog"
        />
      </div>

      <div class="md:tw-px-9 tw-flex tw-gap-1 tw-w-full">
        <div class="tw-flex-auto tw-flex tw-flex-col tw-justify-between">
          <v-btn
            variant="outlined"
            class="tw-rounded-full btn-trade"
            color="white"
            @click="openTradeSigilsDialog"
          >
            <div class="btn-overlay"></div>
            <div class="tw-z-[1]">Trade sigils</div>

            <html-tooltip :text="tradeSigilsTooltip" />

            <trade-sigils-dialog
              ref="tradeSigilsDialogRef"
              :userInfo="userInfo"
            />
          </v-btn>

          <v-btn
            variant="outlined"
            class="tw-rounded-full tw-w-full tw-border-[#4D4D4D]"
            color="white"
            @click="openSigilStatsDialog"
          >
            SIGIL STATS

            <html-tooltip :text="sigilStatsTooltip" />

            <sigil-stats-dialog
              ref="sigilStatsDialogRef"
              :userInfo="userInfo"
            />
          </v-btn>
        </div>

        <div class="tw-w-[45%] lg:tw-w-[42%] tw-min-h-24">
          <div
            class="tw-rounded-3xl tw-uppercase tw-h-full tw-border-[#F14522] tw-border-solid tw-border-4 tw-p-4 tw-text-center tw-text-sm tw-flex tw-justify-center tw-items-center tw-flex-col"
          >
            <div class="tw-font-bold tw-text-[18px] tw-mb-1 tw-max-w-full">
              <Icon
                icon="cryptocurrency-color:avax"
                width="14"
                class="tw-mr-1"
              />
              <!-- 12.52 AVAX -->
              {{ buyCostDisplay.value }} {{ buyCostDisplay.unit }}
            </div>
            <div>SIGIL COST</div>

            <html-tooltip :text="sigilCostTooltip" />
          </div>
        </div>
      </div>
      <follow-list-dialog ref="followListDialogRef" />
      <avatar-preview-dialog ref="avatarPreviewDialogRef" />
    </v-card-text>
  </v-card>
</template>

<style lang="postcss">
.btn-trade {
  @apply tw-w-full;
  background: linear-gradient(120deg, #eb2b7d 0%, #f15323 100%);
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(20px);
  .btn-overlay {
    background: linear-gradient(90deg, #eb2b7d 0.24%, #f15323 99.78%);
    filter: blur(10px) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}
</style>
